import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  BugReport,
  FeaturedPlayList,
  Support,
  Assignment,
  PriorityHigh,
  CheckCircle,
  Pending,
  Cancel,
} from '@mui/icons-material';
import {
  useTicketStatistics,
  TicketStatistics,
} from '../hooks/useTicketStatistics';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color = 'primary',
  subtitle,
}) => (
  <Card elevation={2}>
    <CardContent>
      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Box>
          <Typography variant='h4' color={`${color}.main`} fontWeight='bold'>
            {value}
          </Typography>
          <Typography variant='body1' color='textPrimary' fontWeight='medium'>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant='body2' color='textSecondary'>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box color={`${color}.main`}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

export const TicketStatisticsCard: React.FC = () => {
  const { getTicketStatistics } = useTicketStatistics();
  const [statistics, setStatistics] = useState<TicketStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStatistics = async () => {
      setLoading(true);
      try {
        const stats = await getTicketStatistics();
        setStatistics(stats);
      } catch (error) {
        console.error('Error loading ticket statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, [getTicketStatistics]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!statistics) {
    return (
      <Card>
        <CardContent>
          <Typography variant='h6' color='error'>
            Error al cargar las estadísticas de tickets
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant='h5' gutterBottom fontWeight='bold'>
        Estadísticas de Tickets
      </Typography>

      <Grid container spacing={3}>
        {/* Resumen general */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='Total Tickets'
            value={statistics.totalTickets}
            icon={<Assignment fontSize='large' />}
            color='info'
            subtitle='Todos los tickets creados'
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='Tickets Abiertos'
            value={statistics.openTickets}
            icon={<Pending fontSize='large' />}
            color='primary'
            subtitle='Requieren atención'
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='En Progreso'
            value={statistics.inProgressTickets}
            icon={<Pending fontSize='large' />}
            color='warning'
            subtitle='Siendo trabajados'
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='Resueltos'
            value={statistics.resolvedTickets}
            icon={<CheckCircle fontSize='large' />}
            color='success'
            subtitle='Completados'
          />
        </Grid>

        {/* Estadísticas específicas */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='Alta Prioridad'
            value={statistics.highPriorityTickets}
            icon={<PriorityHigh fontSize='large' />}
            color='error'
            subtitle='Requieren atención urgente'
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='Reportes de Errores'
            value={statistics.bugReports}
            icon={<BugReport fontSize='large' />}
            color='error'
            subtitle='Incidentes reportados'
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='Nuevas Funcionalidades'
            value={statistics.featureRequests}
            icon={<FeaturedPlayList fontSize='large' />}
            color='primary'
            subtitle='Solicitudes de mejora'
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title='Solicitudes de Soporte'
            value={statistics.supportRequests}
            icon={<Support fontSize='large' />}
            color='secondary'
            subtitle='Ayuda requerida'
          />
        </Grid>
      </Grid>

      {/* Resumen por estado */}
      <Box mt={3}>
        <Card>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Resumen por Estado
            </Typography>
            <Box display='flex' flexWrap='wrap' gap={1}>
              <Chip
                label={`${statistics.openTickets} Abiertos`}
                color='primary'
                variant='outlined'
              />
              <Chip
                label={`${statistics.inProgressTickets} En Progreso`}
                color='warning'
                variant='outlined'
              />
              <Chip
                label={`${statistics.resolvedTickets} Resueltos`}
                color='success'
                variant='outlined'
              />
              <Chip
                label={`${statistics.closedTickets} Cerrados`}
                color='default'
                variant='outlined'
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
